import React, { useState, useEffect, useRef } from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    ExpandableSection,
    FormField,
    Header,
    Input,
    Modal,
    Pagination,
    RadioGroup,
    Select,
    SelectProps,
    SpaceBetween,
    Spinner,
    StatusIndicator,
    StatusIndicatorProps,
    Table,
    TextFilter,
} from '@cloudscape-design/components';
import { classifyError, alertTypeForKind, sanitizeTechnicalDetails } from '../../util/errorClassifier';
import { TriggerJobConfirmationModal } from './TriggerJobConfirmationModal';
import {
    jobMonitoringApi,
    seekingAlphaApi,
    trading212AccountApi,
    trading212CFDApi,
    triggerScheduledTask,
} from '../../client/clients';
import {
    JobExecutionStatus,
    TickerResult,
    AccountCash,
    ConciseMarketData,
    TriggerScheduledTaskCredentialKeyEnum,
} from '../../api';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useUser } from '../../context/UserContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useFlashbar } from '../../context/FlashbarContext';
import { formatRelativeTime } from '../../util/formatMoney';

// ─────────────────────────────────────────────────────────────────────────────
// Status helpers
// ─────────────────────────────────────────────────────────────────────────────

// E2: map raw status strings to Cloudscape StatusIndicator types and labels.
// No emojis; rely on icon + text from the StatusIndicator primitive.
const jobStatusToIndicator = (status: string): { type: StatusIndicatorProps.Type; label: string } => {
    switch (status) {
        case 'STARTING':
            return { type: 'info', label: 'Starting' };
        case 'MONITORING':
            return { type: 'in-progress', label: 'Monitoring' };
        case 'SCANNING':
            return { type: 'in-progress', label: 'Scanning' };
        case 'ARTICLE_FOUND':
            return { type: 'info', label: 'Article found' };
        case 'TRADING':
            return { type: 'in-progress', label: 'Trading' };
        case 'COMPLETED':
            return { type: 'success', label: 'Completed' };
        case 'FAILED':
            return { type: 'error', label: 'Failed' };
        default:
            return { type: 'pending', label: status.replace('_', ' ') || 'Unknown' };
    }
};

const JobStatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const { type, label } = jobStatusToIndicator(status);
    return <StatusIndicator type={type}>{label}</StatusIndicator>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Job details modal — Cloudscape Modal (L1).
// ─────────────────────────────────────────────────────────────────────────────

interface JobDetailsModalProps {
    job: JobExecutionStatus;
    onClose: () => void;
}

const JobDetailsModal: React.FC<JobDetailsModalProps> = ({ job, onClose }) => {
    const [tickerResults, setTickerResults] = useState<TickerResult[]>([]);
    const [tickerResultsLoading, setTickerResultsLoading] = useState(false);

    const loadTickerResults = async () => {
        if (!job.executionId) return;
        setTickerResultsLoading(true);
        try {
            const response = await jobMonitoringApi.getTickerResults(job.executionId);
            setTickerResults(response.data || []);
        } catch (error) {
            console.error('Error loading ticker results:', error);
            setTickerResults([]);
        } finally {
            setTickerResultsLoading(false);
        }
    };

    useEffect(() => {
        if (job.executionId) loadTickerResults();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job.executionId]);

    const formatDateTime = (dateTime?: string) => {
        if (!dateTime) return 'N/A';
        try {
            return new Date(dateTime).toLocaleString();
        } catch {
            return dateTime;
        }
    };

    const formatCurrency = (amount?: number, currency?: string) => {
        if (amount === undefined || amount === null) return 'N/A';
        const cur = currency || 'USD';
        return `${cur} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // E1: classify the recorded error and sanitize the raw payload before
    // exposing it as "technical details". This is the modal a user opens after
    // seeing a FAILED job — we want them to read a humane sentence at the top,
    // not a Java stack trace dump.
    const isFailed = job.status === 'FAILED';
    const classified = isFailed
        ? classifyError({
            response: { status: 500, data: { message: job.errorMessage, exceptionClass: undefined } },
            message: job.errorMessage,
        })
        : null;

    // The raw `job` object includes balance fields and may include exception
    // metadata we don't want to leak. `sanitizeTechnicalDetails` strips both.
    const sanitizedJob = sanitizeTechnicalDetails(job);

    return (
        <Modal
            visible={true}
            onDismiss={onClose}
            size="large"
            header="Job execution details"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button onClick={loadTickerResults} loading={tickerResultsLoading}>
                            Refresh ticker results
                        </Button>
                        <Button variant="primary" onClick={onClose}>
                            Close
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween size="m">
                {/* E1: user-facing summary at the top of the modal for failed jobs. */}
                {classified && (
                    <Alert
                        type={alertTypeForKind(classified.kind)}
                        header={classified.userMessage}
                        action={
                            classified.suggestedAction ? (
                                <Button
                                    href={classified.suggestedAction.href}
                                    onClick={classified.suggestedAction.onClick}
                                >
                                    {classified.suggestedAction.label}
                                </Button>
                            ) : undefined
                        }
                    />
                )}

                <SpaceBetween direction="horizontal" size="m">
                    <JobStatusBadge status={job.status || 'UNKNOWN'} />
                    {job.queryDurationMs && (
                        <Box variant="small" color="text-body-secondary">
                            Query: {job.queryDurationMs}ms
                        </Box>
                    )}
                    {job.durationMilliseconds && (
                        <Box variant="small" color="text-body-secondary">
                            Total: {(job.durationMilliseconds / 1000).toFixed(1)}s
                        </Box>
                    )}
                </SpaceBetween>

                <SpaceBetween size="xs" direction="horizontal">
                    <KvPair label="Execution ID" value={job.executionId} />
                    <KvPair label="Started" value={formatDateTime(job.startTime)} />
                    <KvPair label="Last update" value={formatDateTime(job.lastUpdateTime)} />
                    {job.completionTime && <KvPair label="Completed" value={formatDateTime(job.completionTime)} />}
                    <KvPair label="Credential" value={job.credentialKey} />
                </SpaceBetween>

                {job.message && (
                    <Box>
                        <Box variant="awsui-key-label">Status message</Box>
                        <Box>{job.message}</Box>
                    </Box>
                )}


                {(job.articleTitle || job.articleId || (job.tickers && job.tickers.length > 0)) && (
                    <Box>
                        <Box variant="awsui-key-label">Article</Box>
                        {job.articleTitle && <Box>{job.articleTitle}</Box>}
                        {job.articleId && (
                            <Box variant="small" color="text-body-secondary">
                                ID: {job.articleId}
                            </Box>
                        )}
                        {job.tickerValidationMethod && (
                            <Box margin={{ top: 'xs' }}>
                                <StatusIndicator
                                    type={job.tickerValidationMethod === 'IN_HOUSE_VALIDATED' ? 'info' : 'success'}
                                >
                                    {job.tickerValidationMethod === 'IN_HOUSE_VALIDATED'
                                        ? 'In-house validated'
                                        : 'AI verified'}
                                </StatusIndicator>
                            </Box>
                        )}
                        {job.tickers && job.tickers.length > 0 && (
                            <Box margin={{ top: 'xs' }}>
                                <SpaceBetween direction="horizontal" size="xs">
                                    {job.tickers.map((ticker) => (
                                        <span
                                            key={ticker}
                                            style={{
                                                backgroundColor: '#0972d3',
                                                color: 'white',
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                fontSize: 12,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {ticker}
                                        </span>
                                    ))}
                                </SpaceBetween>
                            </Box>
                        )}
                    </Box>
                )}

                {(job.totalTickers !== undefined || job.processedTickers !== undefined ||
                  job.successfulOrders !== undefined || job.failedOrders !== undefined) && (
                    <Box>
                        <Box variant="awsui-key-label">Trading progress</Box>
                        <SpaceBetween direction="horizontal" size="m">
                            {job.totalTickers !== undefined && <KvPair label="Total" value={String(job.totalTickers)} />}
                            {job.processedTickers !== undefined && (
                                <KvPair label="Processed" value={String(job.processedTickers)} />
                            )}
                            {job.successfulOrders !== undefined && (
                                <KvPair label="Successful" value={String(job.successfulOrders)} />
                            )}
                            {job.failedOrders !== undefined && (
                                <KvPair label="Failed" value={String(job.failedOrders)} />
                            )}
                        </SpaceBetween>
                    </Box>
                )}

                {/* E1: raw error message hidden by default — the classified Alert
                    above is the user-facing summary. Power users can still see
                    the original by opening the technical-details footer. */}

                <Box>
                    <Box variant="awsui-key-label">
                        Individual ticker results ({tickerResults.length})
                    </Box>
                    {tickerResultsLoading ? (
                        <Box padding="m"><Spinner /> Loading ticker results…</Box>
                    ) : tickerResults.length === 0 ? (
                        <Box color="text-status-inactive" padding="s">
                            No individual ticker results available yet.
                        </Box>
                    ) : (
                        <SpaceBetween size="xs">
                            {tickerResults.map((ticker, index) => (
                                <Box
                                    key={`${ticker.ticker}-${index}`}
                                    padding="s"
                                    color={ticker.success ? 'text-status-success' : 'text-status-error'}
                                >
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <StatusIndicator type={ticker.success ? 'success' : 'error'}>
                                            {ticker.ticker} ({ticker.accountType})
                                        </StatusIndicator>
                                        {ticker.amount !== undefined && (
                                            <Box variant="small">{formatCurrency(ticker.amount, 'USD')}</Box>
                                        )}
                                    </SpaceBetween>
                                    {ticker.errorMessage && (
                                        <Box variant="small" color="text-status-error">
                                            {ticker.errorMessage}
                                        </Box>
                                    )}
                                </Box>
                            ))}
                        </SpaceBetween>
                    )}
                </Box>

                {/* E1: collapsed-by-default technical details for support hand-off.
                    Stack traces and balance fields are scrubbed at sanitize time. */}
                <ExpandableSection
                    variant="footer"
                    headerText="Technical details (for support)"
                >
                    <Box variant="code">
                        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                            {JSON.stringify(sanitizedJob, null, 2)}
                        </pre>
                    </Box>
                </ExpandableSection>
            </SpaceBetween>
        </Modal>
    );
};

const KvPair: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <Box>
        <Box variant="awsui-key-label">{label}</Box>
        <Box>{value ?? 'N/A'}</Box>
    </Box>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

const credentialOptions: SelectProps.Option[] = [
    // F2: user-facing credential names everywhere.
    { label: 'SeekingAlpha Pro', value: 'SeekingAlphaProCookie' },
    { label: 'SeekingAlpha Premium', value: 'SeekingAlphaPremiumCookie' },
];

export const JobMonitoringPage: React.FC = () => {
    useDocumentTitle('Job monitoring');
    const user = useUser();
    const flashbar = useFlashbar();

    type WatcherJob = JobExecutionStatus & {
        runId?: string;
        watcherIndex?: number;
        callLatencyMs?: number;
        avgCallLatencyMs?: number;
        throttledAtCall?: number;
        monitoringAttempts?: number;
        consecutive403Count?: number;
        timestamp?: string;
        startTime?: string;
    };
    const currentRunIdRef = useRef<string | null>(null);
    const [currentRunId, setCurrentRunId] = useState<string | null>(null);
    const [activeJobs, setActiveJobs] = useState<Map<string, WatcherJob>>(new Map());
    const [jobHistory, setJobHistory] = useState<JobExecutionStatus[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [historyLoading, setHistoryLoading] = useState<boolean>(false);
    const [selectedJob, setSelectedJob] = useState<JobExecutionStatus | null>(null);
    const [showJobModal, setShowJobModal] = useState<boolean>(false);

    // Trigger
    const [triggerJobLoading, setTriggerJobLoading] = useState<boolean>(false);
    const [selectedCredential, setSelectedCredential] = useState<SelectProps.Option>(credentialOptions[0]);
    const [runMode, setRunMode] = useState<'LIVE' | 'MOCK_SCANNER_REAL_BUY' | 'MOCK_BOTH'>('LIVE');
    const [mockArticleId, setMockArticleId] = useState<string>('');
    const [mockInjectAtAttempt, setMockInjectAtAttempt] = useState<string>('');
    const [parallelJobs, setParallelJobs] = useState<string>('10');
    // E4: confirmation modal gate before triggering the live trading job.
    const [triggerConfirmOpen, setTriggerConfirmOpen] = useState<boolean>(false);

    // History table state — T4-C
    const [historyFilterText, setHistoryFilterText] = useState<string>('');
    const [historyCurrentPage, setHistoryCurrentPage] = useState<number>(1);
    const HISTORY_PAGE_SIZE = 10;

    // WebSocket
    const [wsConnected, setWsConnected] = useState<boolean>(false);
    const [wsError, setWsError] = useState<string>('');
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const reconnectAttemptsRef = useRef<number>(0);
    const isConnectingRef = useRef<boolean>(false);
    const maxReconnectAttempts = 5;

    // Live ticker updates
    const [liveTickerUpdates, setLiveTickerUpdates] = useState<Map<string, { ticker: string; marketCap?: number }>>(new Map());

    // Account cash
    const [accountCash, setAccountCash] = useState<AccountCash | null>(null);
    const [cfdCash, setCfdCash] = useState<number | null>(null);
    const [loadingAccountCash, setLoadingAccountCash] = useState<boolean>(false);
    const [orderInProgress, setOrderInProgress] = useState<{ [key: string]: boolean }>({});

    const connectWebSocket = async () => {
        if (isConnectingRef.current) return;
        if (wsRef.current) {
            const state = wsRef.current.readyState;
            if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
        }
        try {
            isConnectingRef.current = true;
            const { tokens } = await fetchAuthSession();
            const token = tokens?.idToken?.toString();
            if (!token) {
                setWsError('Authentication token not available');
                isConnectingRef.current = false;
                return;
            }
            const wsUrl = process.env.REACT_APP_WEBSOCKET_URL;
            if (!wsUrl) {
                setWsError('WebSocket URL not configured');
                isConnectingRef.current = false;
                return;
            }
            if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
                wsRef.current.close();
                wsRef.current = null;
            }
            const ws = new WebSocket(`${wsUrl}?token=${token}`);
            wsRef.current = ws;

            ws.onopen = () => {
                setWsConnected(true);
                setWsError('');
                reconnectAttemptsRef.current = 0;
                isConnectingRef.current = false;
            };

            ws.onmessage = (event) => {
                try {
                    const message = JSON.parse(event.data);
                    setLastUpdate(new Date());
                    if (message.type === 'job-status-update' && message.data) {
                        const job = message.data as WatcherJob;
                        if (!job.executionId || !job.runId) return; // ignore legacy/non-coordinator jobs
                        const isNewRun = job.runId !== currentRunIdRef.current;
                        if (isNewRun) {
                            currentRunIdRef.current = job.runId;
                            setCurrentRunId(job.runId);
                            setActiveJobs(new Map([[job.executionId, job]]));
                        } else {
                            setActiveJobs(prev => {
                                const next = new Map(prev);
                                const existing = next.get(job.executionId!);
                                // Only update if this record is newer (ISO string compare is safe)
                                if (!existing?.timestamp || !job.timestamp || job.timestamp >= existing.timestamp) {
                                    next.set(job.executionId!, job);
                                }
                                return next;
                            });
                        }
                        if (job.status === 'COMPLETED' || job.status === 'FAILED') {
                            handleGetJobHistory(true);
                        }
                    } else if (message.type === 'ticker-result-update') {
                        if (message.data && message.data.ticker) {
                            const ticker = message.data.ticker;
                            const marketCap = message.data.marketCap;
                            setLiveTickerUpdates((prev) => {
                                const newMap = new Map(prev);
                                newMap.set(ticker, { ticker, marketCap });
                                return newMap;
                            });
                            if (!marketCap) {
                                (async () => {
                                    try {
                                        const { data: marketCap } = await seekingAlphaApi.getMarketCap(ticker);
                                        setLiveTickerUpdates((prev) => {
                                            const newMap = new Map(prev);
                                            const existing = newMap.get(ticker);
                                            if (existing) newMap.set(ticker, { ...existing, marketCap });
                                            return newMap;
                                        });
                                    } catch (error) {
                                        console.error(`Error fetching market cap for ${ticker}:`, error);
                                    }
                                })();
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error parsing WebSocket message:', error);
                }
            };

            ws.onerror = () => {
                setWsError('WebSocket connection error');
                isConnectingRef.current = false;
            };

            ws.onclose = (event) => {
                setWsConnected(false);
                isConnectingRef.current = false;
                if (event.code !== 1000 && reconnectAttemptsRef.current < maxReconnectAttempts) {
                    const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000);
                    reconnectTimeoutRef.current = setTimeout(() => {
                        reconnectAttemptsRef.current += 1;
                        connectWebSocket();
                    }, delay);
                }
            };
        } catch (error) {
            console.error('Error connecting to WebSocket:', error);
            setWsError('Failed to establish WebSocket connection');
            isConnectingRef.current = false;
        }
    };

    const disconnectWebSocket = () => {
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        if (wsRef.current) {
            wsRef.current.close(1000, 'User initiated disconnect');
            wsRef.current = null;
        }
        setWsConnected(false);
    };

    useEffect(() => {
        handleGetCurrentJob();
        handleGetJobHistory();
        loadAccountCash();
        connectWebSocket();
        return () => disconnectWebSocket();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadAccountCash = async () => {
        setLoadingAccountCash(true);
        try {
            const isaResponse = await trading212AccountApi.getAccountCash();
            setAccountCash(isaResponse.data);
            const { data: cfdResponse } = await trading212CFDApi.getAccountsSummary();
            if (cfdResponse.cash?.free) setCfdCash(cfdResponse.cash.free);
        } catch (error) {
            console.error('Error loading account cash:', error);
        } finally {
            setLoadingAccountCash(false);
        }
    };

    // No confirmation modal here by design: this panel is admin-only and used for
    // time-sensitive manual intervention during live job execution. The ISA/CFD pages
    // have the full OrderConfirmationModal gate for routine trading.
    const handlePlaceOrder = async (ticker: string, accountType: 'ISA' | 'CFD', fraction: number) => {
        const orderKey = `${ticker}-${accountType}-${fraction}`;
        if (orderInProgress[orderKey]) return;
        setOrderInProgress((prev) => ({ ...prev, [orderKey]: true }));
        try {
            const availableCash = accountType === 'ISA' ? accountCash?.free : cfdCash;
            if (!availableCash || availableCash <= 0) {
                flashbar.push({
                    type: 'error',
                    header: `Order skipped — no cash`,
                    content: `No available cash in the ${accountType} account.`,
                });
                return;
            }
            const orderValue = Number((availableCash * fraction).toFixed(2));
            if (accountType === 'ISA') {
                await trading212AccountApi.placeValueOrder1({ ticker, value: orderValue });
                flashbar.push({
                    type: 'success',
                    header: 'ISA order placed',
                    content: `${ticker}: $${orderValue.toFixed(2)}`,
                });
            } else {
                const { data: stockPriceResponse } = await trading212AccountApi.getStockPrice1(ticker.trim().toUpperCase());
                const targetPrice = (stockPriceResponse as ConciseMarketData).close || 0;
                if (targetPrice <= 0) {
                    flashbar.push({
                        type: 'error',
                        header: 'Invalid stock price',
                        content: `Could not place CFD order for ${ticker}.`,
                    });
                    return;
                }
                await trading212CFDApi.openPosition({
                    instrumentCode: ticker,
                    targetPrice,
                    quantity: orderValue * 4.95,
                });
                flashbar.push({
                    type: 'success',
                    header: 'CFD position opened',
                    content: `${ticker}: $${orderValue.toFixed(2)}`,
                });
            }
            await loadAccountCash();
        } catch (error: any) {
            console.error(`Error placing ${accountType} order for ${ticker}:`, error);
            const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
            flashbar.push({
                type: 'error',
                header: `${accountType} order failed`,
                content: `${ticker}: ${errorMsg}`,
            });
        } finally {
            setOrderInProgress((prev) => ({ ...prev, [orderKey]: false }));
        }
    };

    const handleGetCurrentJob = async (silent: boolean = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await jobMonitoringApi.getLiveJobStatus();
            if (response.data?.executionId) {
                setActiveJobs(prev => {
                    const next = new Map(prev);
                    next.set(response.data.executionId!, response.data as WatcherJob);
                    return next;
                });
                if (!silent) handleGetJobHistory(true);
            }
        } catch (err: any) {
            console.error('Error fetching current job:', err);
            if (!silent) {
                flashbar.push({
                    type: 'error',
                    header: 'Failed to fetch current job status',
                    content: err.response?.data?.message || err.message,
                });
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleGetJobHistory = async (silent: boolean = false) => {
        if (!silent) setHistoryLoading(true);
        try {
            const response = await jobMonitoringApi.getJobHistory(10);
            setJobHistory(response.data || []);
        } catch (err: any) {
            console.error('Error fetching job history:', err);
            if (!silent) {
                flashbar.push({
                    type: 'error',
                    header: 'Failed to fetch job history',
                    content: err.response?.data?.message || err.message,
                });
            }
        } finally {
            if (!silent) setHistoryLoading(false);
        }
    };

    const handleViewJobDetails = (job: JobExecutionStatus) => {
        setSelectedJob(job);
        setShowJobModal(true);
    };

    const formatDateTime = (dateTime?: string) => {
        if (!dateTime) return 'N/A';
        try {
            return new Date(dateTime).toLocaleString();
        } catch {
            return dateTime;
        }
    };

    /**
     * E4: actual trigger call. Only invoked from the confirmation modal's
     * primary action — never directly from the page button.
     */
    const handleConfirmTriggerScheduledJob = async () => {
        setTriggerJobLoading(true);
        try {
            const credentialKey = selectedCredential.value as TriggerScheduledTaskCredentialKeyEnum;
            const articleIdParam = runMode === 'LIVE' ? undefined : mockArticleId.trim() || undefined;
            const attemptParam = runMode === 'LIVE'
                ? undefined
                : (mockInjectAtAttempt.trim() ? parseInt(mockInjectAtAttempt.trim(), 10) : undefined);
            const parallelJobsParam = parallelJobs.trim() ? parseInt(parallelJobs.trim(), 10) : undefined;
            const response = await triggerScheduledTask(
                credentialKey,
                runMode,
                articleIdParam,
                attemptParam,
                parallelJobsParam,
            );
            const data = response.data;
            flashbar.push({
                type: 'success',
                header: 'Job triggered',
                content: `${data.message ?? ''} (${data.credentialKey ?? credentialKey})`,
            });
            setTriggerConfirmOpen(false);
            setTimeout(() => handleGetCurrentJob(), 2000);
        } catch (err: unknown) {
            console.error('Error triggering scheduled job:', err);
            const classified = classifyError(err, { onRetry: handleConfirmTriggerScheduledJob });
            flashbar.push({
                type: 'error',
                header: 'Failed to trigger job',
                content: classified.userMessage,
            });
        } finally {
            setTriggerJobLoading(false);
        }
    };

    const formatDuration = (durationMs?: number) => {
        if (!durationMs) return 'N/A';
        if (durationMs < 1000) return `${durationMs}ms`;
        if (durationMs < 60000) return `${(durationMs / 1000).toFixed(1)}s`;
        return `${(durationMs / 60000).toFixed(1)}m`;
    };

    // E6: derive WebSocket status indicator from real connection state.
    const wsStatusType: StatusIndicatorProps.Type = wsConnected
        ? 'success'
        : wsError
            ? 'error'
            : 'in-progress';
    const wsStatusLabel = wsConnected
        ? 'Live updates active'
        : wsError
            ? 'Connection error'
            : 'Connecting…';

    return (
        <Container>
            <SpaceBetween size="l">
                <Header
                    variant="h1"
                    description="Real-time monitoring of scheduled trading job executions over WebSocket."
                    actions={
                        <Button
                            iconName="refresh"
                            variant="icon"
                            ariaLabel="Refresh"
                            loading={loading}
                            onClick={() => handleGetCurrentJob()}
                        />
                    }
                >
                    Job monitoring
                </Header>

                {/* E6: WebSocket status as Cloudscape StatusIndicator with aria-live polite. */}
                <div aria-live="polite">
                    <SpaceBetween direction="horizontal" size="s">
                        <StatusIndicator type={wsStatusType}>{wsStatusLabel}</StatusIndicator>
                        {lastUpdate && wsConnected && (
                            <Box variant="small" color="text-body-secondary">
                                Last update: {formatRelativeTime(lastUpdate)}
                            </Box>
                        )}
                    </SpaceBetween>
                </div>

                {/* E3: Manual trigger — Container + Header instead of yellow alert panel. */}
                {user.isAdmin && (
                    <Container
                        header={
                            <Header
                                variant="h2"
                                description="Run the scheduled trading job now, bypassing the trading-day window."
                            >
                                Manual scheduled job trigger
                            </Header>
                        }
                    >
                        <SpaceBetween size="m">
                            <FormField label="Credential">
                                <Select
                                    selectedOption={selectedCredential}
                                    onChange={({ detail }) =>
                                        setSelectedCredential(detail.selectedOption as SelectProps.Option)
                                    }
                                    options={credentialOptions}
                                />
                            </FormField>

                            <FormField label="Parallel watchers" constraintText="1–20">
                                <Input
                                    type="number"
                                    value={parallelJobs}
                                    onChange={({ detail }) => setParallelJobs(detail.value)}
                                    placeholder="10"
                                />
                            </FormField>

                            <FormField label="Run mode" description="Pick how the manual trigger should run.">
                                <RadioGroup
                                    value={runMode}
                                    onChange={({ detail }) =>
                                        setRunMode(detail.value as 'LIVE' | 'MOCK_SCANNER_REAL_BUY' | 'MOCK_BOTH')
                                    }
                                    items={[
                                        { value: 'LIVE', label: 'Full live run (identical to scheduled)' },
                                        { value: 'MOCK_SCANNER_REAL_BUY', label: 'Find article X, real buy' },
                                        { value: 'MOCK_BOTH', label: 'Mock everything (no real orders)' },
                                    ]}
                                />
                            </FormField>

                            {runMode !== 'LIVE' && (
                                <>
                                    <FormField
                                        label="Mock article ID"
                                        description="Article to inject into the SeekingAlpha listing response."
                                    >
                                        <Input
                                            value={mockArticleId}
                                            onChange={({ detail }) => setMockArticleId(detail.value)}
                                            placeholder="e.g. 4793521"
                                        />
                                    </FormField>
                                    <FormField
                                        label="Inject at attempt"
                                        description="Which monitoring attempt the mock article should appear at."
                                        constraintText="1–1000"
                                    >
                                        <Input
                                            type="number"
                                            value={mockInjectAtAttempt}
                                            onChange={({ detail }) => setMockInjectAtAttempt(detail.value)}
                                            placeholder="e.g. 5"
                                        />
                                    </FormField>
                                </>
                            )}

                            <Button
                                variant="primary"
                                loading={triggerJobLoading}
                                disabled={
                                    runMode !== 'LIVE' &&
                                    (!mockArticleId.trim() || !mockInjectAtAttempt.trim())
                                }
                                onClick={() => setTriggerConfirmOpen(true)}
                            >
                                {triggerJobLoading ? 'Triggering…' : 'Trigger job'}
                            </Button>
                        </SpaceBetween>
                    </Container>
                )}

                {/* Live ticker updates */}
                {liveTickerUpdates.size > 0 && (
                    <Container
                        header={
                            <Header
                                variant="h2"
                                actions={
                                    <SpaceBetween direction="horizontal" size="xs">
                                        {accountCash && (
                                            <Box>
                                                ISA: ${accountCash.free?.toFixed(2) || '0.00'}
                                            </Box>
                                        )}
                                        {cfdCash !== null && <Box>CFD: ${cfdCash.toFixed(2)}</Box>}
                                        <Button
                                            iconName="refresh"
                                            loading={loadingAccountCash}
                                            onClick={loadAccountCash}
                                        >
                                            Refresh cash
                                        </Button>
                                    </SpaceBetween>
                                }
                            >
                                Live ticker updates
                            </Header>
                        }
                    >
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                            {Array.from(liveTickerUpdates.values()).map((tickerData, index) => (
                                <div
                                    key={`${tickerData.ticker}-${index}`}
                                    style={{
                                        padding: 12,
                                        border: '1px solid #d5dbdb',
                                        borderRadius: 8,
                                        backgroundColor: '#ffffff',
                                        minWidth: 240,
                                        maxWidth: 320,
                                    }}
                                >
                                    <SpaceBetween size="xs">
                                        <SpaceBetween direction="horizontal" size="xs">
                                            <span
                                                style={{
                                                    backgroundColor: '#0972d3',
                                                    color: 'white',
                                                    padding: '4px 10px',
                                                    borderRadius: 4,
                                                    fontSize: 14,
                                                    fontWeight: 700,
                                                }}
                                            >
                                                {tickerData.ticker}
                                            </span>
                                            <Box variant="small" color="text-body-secondary">
                                                {tickerData.marketCap
                                                    ? `Market cap: $${(tickerData.marketCap / 1e9).toFixed(2)}B`
                                                    : 'Market cap: N/A'}
                                            </Box>
                                        </SpaceBetween>

                                        {user.isAdmin && (
                                            <SpaceBetween size="xs">
                                                <Box variant="small">ISA</Box>
                                                <SpaceBetween direction="horizontal" size="xs">
                                                    {[{ label: '1/4', value: 0.25 }, { label: '1/3', value: 0.33 }, { label: '1/2', value: 0.5 }, { label: 'All', value: 1 }].map((btn) => {
                                                        const key = `${tickerData.ticker}-ISA-${btn.value}`;
                                                        return (
                                                            <Button
                                                                key={key}
                                                                onClick={() => handlePlaceOrder(tickerData.ticker, 'ISA', btn.value)}
                                                                loading={orderInProgress[key]}
                                                                disabled={!accountCash?.free || orderInProgress[key]}
                                                            >
                                                                {btn.label}
                                                            </Button>
                                                        );
                                                    })}
                                                </SpaceBetween>
                                                <Box variant="small">CFD</Box>
                                                <SpaceBetween direction="horizontal" size="xs">
                                                    {[{ label: '1/4', value: 0.25 }, { label: '1/3', value: 0.33 }, { label: '1/2', value: 0.5 }, { label: 'All', value: 1 }].map((btn) => {
                                                        const key = `${tickerData.ticker}-CFD-${btn.value}`;
                                                        return (
                                                            <Button
                                                                key={key}
                                                                onClick={() => handlePlaceOrder(tickerData.ticker, 'CFD', btn.value)}
                                                                loading={orderInProgress[key]}
                                                                disabled={!cfdCash || orderInProgress[key]}
                                                            >
                                                                {btn.label}
                                                            </Button>
                                                        );
                                                    })}
                                                </SpaceBetween>
                                            </SpaceBetween>
                                        )}
                                    </SpaceBetween>
                                </div>
                            ))}
                        </div>
                    </Container>
                )}

                {/* Active watcher jobs */}
                <Container header={
                    <Header variant="h2" counter={activeJobs.size > 0 ? `(${activeJobs.size})` : undefined}>
                        Active watchers
                    </Header>
                }>
                    {loading && activeJobs.size === 0 ? (
                        <Box textAlign="center" padding="l">
                            <SpaceBetween size="xs">
                                <Spinner />
                                <Box>Loading current job status…</Box>
                            </SpaceBetween>
                        </Box>
                    ) : activeJobs.size > 0 ? (
                        <SpaceBetween size="s">
                            {Array.from(activeJobs.values())
                                .sort((a, b) => (a.watcherIndex ?? 999) - (b.watcherIndex ?? 999))
                                .map((job) => {
                                    const lastUpdate = job.timestamp || job.lastUpdateTime;
                                    const durationMs = job.startTime && job.timestamp
                                        ? new Date(job.timestamp).getTime() - new Date(job.startTime).getTime()
                                        : job.durationMilliseconds;
                                    const latencyLabel = job.avgCallLatencyMs
                                        ? `avg ${job.avgCallLatencyMs}ms`
                                        : job.callLatencyMs !== undefined
                                        ? `${job.callLatencyMs}ms`
                                        : null;
                                    return (
                                    <div key={job.executionId} style={{ borderLeft: '3px solid #0972d3', paddingLeft: '12px' }}>
                                        <SpaceBetween size="xs">
                                            <SpaceBetween direction="horizontal" size="s">
                                                <Box variant="awsui-key-label">
                                                    {job.watcherIndex !== undefined ? `Watcher ${job.watcherIndex}` : 'Watcher'}
                                                    {latencyLabel && <span style={{ fontWeight: 'normal', marginLeft: 8, color: '#5f6b7a' }}>{latencyLabel}</span>}
                                                </Box>
                                                <JobStatusBadge status={job.status || 'UNKNOWN'} />
                                                <Button variant="inline-link" onClick={() => handleViewJobDetails(job)}>
                                                    View details
                                                </Button>
                                            </SpaceBetween>
                                            <SpaceBetween direction="horizontal" size="m">
                                                <KvPair label="Execution ID" value={job.executionId} />
                                                <KvPair label="Duration" value={formatDuration(durationMs)} />
                                                <KvPair label="Last update" value={formatDateTime(lastUpdate)} />
                                                <KvPair label="Calls" value={job.monitoringAttempts !== undefined ? String(job.monitoringAttempts) : '—'} />
                                            </SpaceBetween>
                                            {job.throttledAtCall !== undefined ? (
                                                <Box color="text-status-error">
                                                    Throttled at call {job.throttledAtCall}
                                                    {job.callLatencyMs !== undefined ? ` — last latency ${job.callLatencyMs}ms` : ''}
                                                    {job.consecutive403Count ? ` (${job.consecutive403Count} consecutive 403s)` : ''}
                                                </Box>
                                            ) : job.message ? (
                                                <Box color="text-status-inactive">{job.message}</Box>
                                            ) : null}
                                        </SpaceBetween>
                                    </div>
                                    );
                                })}
                        </SpaceBetween>
                    ) : (
                        <Box textAlign="center" color="text-status-inactive" padding="l">
                            No active job executions found. The scheduled job is waiting for the next execution window.
                        </Box>
                    )}
                </Container>

                {/* History — T4-C: Cloudscape Table replaces card list. */}
                {(() => {
                    // Credential key → human label mapping (mirrors credentialOptions).
                    const credentialLabel = (key?: string): string => {
                        if (!key) return 'Unknown';
                        if (key === 'SeekingAlphaProCookie') return 'SeekingAlpha Pro';
                        if (key === 'SeekingAlphaPremiumCookie') return 'SeekingAlpha Premium';
                        return key;
                    };

                    const truncate = (s?: string, max = 40): string => {
                        if (!s) return '—';
                        return s.length <= max ? s : `${s.slice(0, max)}…`;
                    };

                    const resultSummary = (job: JobExecutionStatus): string => {
                        const parts: string[] = [];
                        if (job.totalTickers !== undefined) parts.push(`${job.totalTickers} tickers`);
                        if (job.successfulOrders !== undefined) parts.push(`${job.successfulOrders} ok`);
                        if (job.failedOrders !== undefined && job.failedOrders > 0) parts.push(`${job.failedOrders} failed`);
                        return parts.length > 0 ? parts.join(', ') : '—';
                    };

                    // Client-side filter on visible text fields.
                    const filterLower = historyFilterText.toLowerCase();
                    const filtered = jobHistory.filter((job) => {
                        if (!filterLower) return true;
                        return (
                            (job.status || '').toLowerCase().includes(filterLower) ||
                            credentialLabel(job.credentialKey).toLowerCase().includes(filterLower) ||
                            (job.articleTitle || '').toLowerCase().includes(filterLower) ||
                            (job.startTime || '').toLowerCase().includes(filterLower)
                        );
                    });

                    // Sort descending by startTime (default).
                    const sorted = [...filtered].sort((a, b) => {
                        const ta = a.startTime ? new Date(a.startTime).getTime() : 0;
                        const tb = b.startTime ? new Date(b.startTime).getTime() : 0;
                        return tb - ta;
                    });

                    const totalPages = Math.max(1, Math.ceil(sorted.length / HISTORY_PAGE_SIZE));
                    const safeCurrentPage = Math.min(historyCurrentPage, totalPages);
                    const pageItems = sorted.slice(
                        (safeCurrentPage - 1) * HISTORY_PAGE_SIZE,
                        safeCurrentPage * HISTORY_PAGE_SIZE
                    );

                    return (
                        <Table
                            contentDensity="compact"
                            loading={historyLoading}
                            loadingText="Loading job history…"
                            items={pageItems}
                            columnDefinitions={[
                                {
                                    id: 'when',
                                    header: 'When',
                                    cell: (job: JobExecutionStatus) => formatDateTime(job.startTime),
                                    sortingField: 'startTime',
                                },
                                {
                                    id: 'status',
                                    header: 'Status',
                                    cell: (job: JobExecutionStatus) => (
                                        <JobStatusBadge status={job.status || 'UNKNOWN'} />
                                    ),
                                },
                                {
                                    id: 'duration',
                                    header: 'Duration',
                                    cell: (job: JobExecutionStatus) =>
                                        formatDuration(job.durationMilliseconds),
                                },
                                {
                                    id: 'credential',
                                    header: 'Credential',
                                    cell: (job: JobExecutionStatus) =>
                                        credentialLabel(job.credentialKey),
                                },
                                {
                                    id: 'article',
                                    header: 'Article',
                                    cell: (job: JobExecutionStatus) =>
                                        truncate(job.articleTitle, 40),
                                },
                                {
                                    id: 'result',
                                    header: 'Result',
                                    cell: (job: JobExecutionStatus) => resultSummary(job),
                                },
                                {
                                    id: 'actions',
                                    header: '',
                                    cell: (job: JobExecutionStatus) => (
                                        <Button
                                            variant="inline-link"
                                            onClick={() => handleViewJobDetails(job)}
                                        >
                                            View details
                                        </Button>
                                    ),
                                },
                            ]}
                            filter={
                                <TextFilter
                                    filteringText={historyFilterText}
                                    filteringPlaceholder="Filter history"
                                    onChange={({ detail }) => {
                                        setHistoryFilterText(detail.filteringText);
                                        setHistoryCurrentPage(1);
                                    }}
                                    countText={
                                        filterLower
                                            ? `${filtered.length} match${filtered.length === 1 ? '' : 'es'}`
                                            : undefined
                                    }
                                />
                            }
                            pagination={
                                <Pagination
                                    currentPageIndex={safeCurrentPage}
                                    pagesCount={totalPages}
                                    onChange={({ detail }) =>
                                        setHistoryCurrentPage(detail.currentPageIndex)
                                    }
                                />
                            }
                            empty={
                                <Box textAlign="center" color="inherit" padding="l">
                                    <b>No job history</b>
                                    <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                                        No job execution history available.
                                    </Box>
                                </Box>
                            }
                            header={
                                <Header
                                    variant="h2"
                                    counter={`(${jobHistory.length})`}
                                    actions={
                                        <Button
                                            iconName="refresh"
                                            variant="icon"
                                            ariaLabel="Refresh history"
                                            loading={historyLoading}
                                            onClick={() => handleGetJobHistory()}
                                        />
                                    }
                                >
                                    Recent job history
                                </Header>
                            }
                        />
                    );
                })()}

                {showJobModal && selectedJob && (
                    <JobDetailsModal
                        job={selectedJob}
                        onClose={() => {
                            setShowJobModal(false);
                            setSelectedJob(null);
                        }}
                    />
                )}

                {/* E4: confirmation gate before the live trading job fires. */}
                {triggerConfirmOpen && (
                    <TriggerJobConfirmationModal
                        credentialLabel={selectedCredential.label ?? String(selectedCredential.value ?? '')}
                        runMode={runMode}
                        mockArticleId={runMode !== 'LIVE' ? mockArticleId : undefined}
                        mockInjectAtAttempt={runMode !== 'LIVE' ? mockInjectAtAttempt : undefined}
                        onRun={handleConfirmTriggerScheduledJob}
                        onDismiss={() => {
                            if (triggerJobLoading) return;
                            setTriggerConfirmOpen(false);
                        }}
                        loading={triggerJobLoading}
                    />
                )}
            </SpaceBetween>
        </Container>
    );
};
